import type { GameCommand, Vec2 } from "../engine/types";

export function angleFromDrag(
  start: { x: number; y: number },
  end: { x: number; y: number },
  deadZone = 16
): number | null {
  const x = end.x - start.x;
  const y = end.y - start.y;
  return Math.hypot(x, y) > deadZone ? Math.atan2(y, x) : null;
}

export function isTapGesture(
  start: { x: number; y: number },
  end: { x: number; y: number },
  deadZone = 16
): boolean {
  return Math.hypot(end.x - start.x, end.y - start.y) <= deadZone;
}

export function pointerGestureDeadZone(pointerType: string): number {
  return pointerType === "touch" ? 36 : 16;
}

export class InputController {
  private aimAngle = Math.PI;
  private aimDirty = false;
  private rotation = 0;
  private gustRequested = false;
  private fireRequested = false;
  private fireAt: Vec2 | null = null;
  private pointerStart: { x: number; y: number; pointerType: string } | null = null;
  private gamepadGustHeld = false;
  private gamepadFireHeld = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly toWorld: (clientX: number, clientY: number) => Vec2
  ) {
    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointerup", this.onPointerUp);
    canvas.addEventListener("pointercancel", this.onPointerCancel);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
  }

  poll(): GameCommand {
    this.pollGamepad();
    const command: GameCommand = {
      rotation: this.rotation,
      gust: this.gustRequested,
      fire: this.fireRequested
    };
    if (this.fireAt) {
      command.fireAt = this.fireAt;
    }
    if (this.aimDirty) {
      command.aimAngle = this.aimAngle;
      this.aimDirty = false;
    }
    this.gustRequested = false;
    this.fireRequested = false;
    this.fireAt = null;
    return command;
  }

  requestGust(): void {
    this.gustRequested = true;
  }

  destroy(): void {
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointerup", this.onPointerUp);
    this.canvas.removeEventListener("pointercancel", this.onPointerCancel);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    this.pointerStart = { x: event.clientX, y: event.clientY, pointerType: event.pointerType };
    try {
      this.canvas.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic accessibility tests and older embedded browsers may not expose active capture.
    }
  };

  private readonly onPointerUp = (event: PointerEvent): void => {
    if (this.pointerStart) {
      const end = { x: event.clientX, y: event.clientY };
      const deadZone = pointerGestureDeadZone(this.pointerStart.pointerType);
      if (isTapGesture(this.pointerStart, end, deadZone)) {
        this.fireAt = this.toWorld(event.clientX, event.clientY);
      } else {
        this.updateAimFromDrag(event.clientX, event.clientY, deadZone);
      }
    }
    this.pointerStart = null;
  };

  private readonly onPointerCancel = (): void => {
    this.pointerStart = null;
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.code === "KeyA" || event.code === "ArrowLeft") {
      this.rotation = -1;
      event.preventDefault();
    }
    if (event.code === "KeyD" || event.code === "ArrowRight") {
      this.rotation = 1;
      event.preventDefault();
    }
    if (event.code === "Space" && !event.repeat) {
      this.requestGust();
      event.preventDefault();
    }
    if ((event.code === "KeyF" || event.code === "Enter") && !event.repeat) {
      this.fireRequested = true;
      event.preventDefault();
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if ((event.code === "KeyA" || event.code === "ArrowLeft") && this.rotation < 0) {
      this.rotation = 0;
    }
    if ((event.code === "KeyD" || event.code === "ArrowRight") && this.rotation > 0) {
      this.rotation = 0;
    }
  };

  private updateAimFromDrag(clientX: number, clientY: number, deadZone: number): void {
    if (!this.pointerStart) {
      return;
    }
    const angle = angleFromDrag(this.pointerStart, { x: clientX, y: clientY }, deadZone);
    if (angle !== null) {
      this.aimAngle = angle;
      this.aimDirty = true;
    }
  }

  private pollGamepad(): void {
    const gamepad = navigator.getGamepads?.()[0];
    if (!gamepad) {
      return;
    }
    const x = gamepad.axes[0] ?? 0;
    const y = gamepad.axes[1] ?? 0;
    if (Math.hypot(x, y) > 0.22) {
      this.aimAngle = Math.atan2(y, x);
      this.aimDirty = true;
    }
    const firePressed = gamepad.buttons[0]?.pressed ?? false;
    if (firePressed && !this.gamepadFireHeld) {
      this.fireRequested = true;
    }
    this.gamepadFireHeld = firePressed;
    const gustPressed = gamepad.buttons[1]?.pressed ?? false;
    if (gustPressed && !this.gamepadGustHeld) {
      this.requestGust();
    }
    this.gamepadGustHeld = gustPressed;
  }
}
